import { useEffect, useRef, useState } from "react";
import styles from "../styles/SupporterModal.module.css";
import { generateAuthToken } from "../pages/api/utils";
import apiService from "../pages/api/apiService";
import { toast } from "react-toastify";
import { useDispatch, useSelector } from "react-redux";
import { setAuth } from "../store/authSlice";
import { useRouter } from "next/router";
import ReCAPTCHA from "react-google-recaptcha";

// Languages login
import enlogin from "../../locales/en/login.json";
import eslogin from "../../locales/es/login.json";

export default function CreateAccountPopup({ onClose }) {
    const [name, setName] = useState("");
    const [email, setEmail] = useState("");
    const [nameError, setNameError] = useState("");
    const [emailError, setEmailError] = useState("");
    const [captchaToken, setCaptchaToken] = useState(null);
    const [showForgotPassword, setShowForgotPassword] = useState(false);
    const [isSubmitting, setIsSubmitting] = useState(false);

    const recaptchaRef = useRef(null);
    const Languages = useSelector((state) => state.auth.lang);
    const [loginText, setLoginText] = useState(enlogin);

    const dispatch = useDispatch();
    const router = useRouter();

    // 🔹 Language Switch
    useEffect(() => {
        setLoginText(Languages === "en" ? enlogin : eslogin);
    }, [Languages]);

    // 🔹 Email Validation
    const validateEmail = (value) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value);

    const handleNameChange = (e) => {
        const value = e.target.value;
        setName(value);
        setNameError(value ? "" : (loginText?.full_name_required || "Name is required"));
    };

    const handleEmailChange = (e) => {
        const value = e.target.value;
        setEmail(value);
        if (!value) {
            setEmailError(loginText?.email_required || "Email is required");
        } else if (!validateEmail(value)) {
            setEmailError(loginText?.email_invalid || "Invalid email format");
        } else {
            setEmailError("");
        }
    };

    // 🔹 Submit Handler
    const handleSubmit = async (e) => {
        e.preventDefault();
        setIsSubmitting(true);

        let valid = true;

        // Check CAPTCHA
        if (!captchaToken && !showForgotPassword) {
            toast.error("Please verify the reCAPTCHA", { position: "top-right", autoClose: 3000 });
            setIsSubmitting(false);
            return;
        }

        // Validate fields
        if (!name && !showForgotPassword) {
            setNameError(loginText?.full_name_required || "Name is required");
            valid = false;
        }

        if (!email) {
            setEmailError(loginText?.email_required || "Email is required");
            valid = false;
        } else if (!validateEmail(email)) {
            setEmailError(loginText?.email_invalid || "Invalid email format");
            valid = false;
        }

        if (!valid) {
            setIsSubmitting(false);
            return;
        }

        // 🔹 Forgot Password Logic
        if (showForgotPassword) {
            const authToken = generateAuthToken(email + "FORGOT_PASSWORD");
            const requestData = { email, auth_token: authToken };

            try {
                const response = await apiService.post("/forgot_password", requestData);
                if (response.success) {
                    toast.success(response.message || "Password reset link sent to your email!", {
                        position: "top-right",
                        autoClose: 3000,
                    });
                    setShowForgotPassword(false);
                    setEmail("");
                } else {
                    toast.error(response.message || "Failed to send reset link", {
                        position: "top-right",
                        autoClose: 3000,
                    });
                }
            } catch (error) {
                toast.error("Something went wrong. Please try again.", {
                    position: "top-right",
                    autoClose: 3000,
                });
            }
            setIsSubmitting(false);
            return;
        }

        // 🔹 Registration Logic
        const authTokenGen = generateAuthToken(email + "USER_REGISTER");

        const requestData = {
            full_name: name,
            email,
            recaptcha: captchaToken,
            auth_token: authTokenGen,
        };

        try {
            const response = await apiService.post("/user_register", requestData);

            if (response.success) {
                toast.success(response.message || "Registration successful", {
                    position: "top-right",
                    autoClose: 3000,
                });

                if (recaptchaRef.current) recaptchaRef.current.reset();
                setCaptchaToken(null);
                setName("");
                setEmail("");
                onClose();

                setTimeout(() => {
                    router.push("/login-instructions");
                }, 1000);
            } else {
                toast.error(response.message || "Registration failed", {
                    position: "top-right",
                    autoClose: 3000,
                });
            }
        } catch (error) {
            toast.error("Something went wrong. Please try again.", {
                position: "top-right",
                autoClose: 3000,
            });
        }
        setIsSubmitting(false);
    };

    return (
        <div className={`${styles.overlay} d-flex justify-content-center align-items-center`}
        onClick={(e) => {
        if (e.target.classList.contains(styles.overlay)) {
          onClose();
        }
      }}>
            <div className={styles.bgCircle}></div>
            <div className={`${styles.card} border-0 rounded-4 p-4 position-relative`}>
                {/* Close Button */}
                <button
                    onClick={onClose}
                    className={styles.closeIcon}
                    style={{ width: "32px", height: "32px" }}
                >
                    ✕
                </button>

                {/* Forgot Password View */}
                {showForgotPassword ? (
                    <>
                        <div className="text-center mb-3">
                            <h3 className="login-heading mb-3">
                                {loginText?.forgot_password_title || "Forgot Password"}
                            </h3>
                            <p className="paragraph-2">
                                {loginText?.forgot_password_description ||
                                    "Enter your email to reset your password."}
                            </p>
                        </div>

                        {/* Email Field */}
                        <div className="mb-3 text-start">
                            <input
                                type="email"
                                className="input w-input form-control"
                                placeholder={`${loginText?.email}*`}
                                value={email}
                                onChange={handleEmailChange}
                                required
                                disabled={isSubmitting}
                            />
                            {emailError && <small className="text-danger">{emailError}</small>}
                        </div>

                        <button
                            onClick={handleSubmit}
                            disabled={isSubmitting}
                            className="cta-btn w-100 d-flex justify-content-center"
                        >
                            {isSubmitting ? "Please wait..." : (loginText?.submit || "Submit")}
                        </button>

                        <div className="text-center mt-3">
                            <a
                                href="#"
                                className="link"
                                onClick={(e) => {
                                    e.preventDefault();
                                    setShowForgotPassword(false);
                                }}
                            >
                                {loginText?.back_to_register || "Back to Register"}
                            </a>
                        </div>
                    </>
                ) : (
                    <>
                        {/* Register Screen */}
                        <div className="text-center mb-3">
                            <h3 className="login-heading mb-3">{loginText?.register_title}</h3>
                            <p className="paragraph-2">{loginText?.description}</p>
                        </div>

                        {/* Name Field */}
                        <div className="mb-3 text-start">
                            <input
                                type="text"
                                className="input w-input form-control"
                                placeholder={`${loginText?.full_name}*`}
                                value={name}
                                onChange={handleNameChange}
                                required
                                disabled={isSubmitting}
                            />
                            {nameError && <small className="text-danger">{nameError}</small>}
                        </div>

                        {/* Email Field */}
                        <div className="mb-3 text-start">
                            <input
                                type="email"
                                className="input w-input form-control"
                                placeholder={`${loginText?.email}*`}
                                value={email}
                                onChange={handleEmailChange}
                                required
                                disabled={isSubmitting}
                            />
                            {emailError && <small className="text-danger">{emailError}</small>}
                        </div>

                        {/* Forgot Password Link */}
                        <div className="text-start mb-3">
                            <a
                                href="#"
                                className="link"
                                onClick={(e) => {
                                    e.preventDefault();
                                    setShowForgotPassword(true);
                                }}
                            >
                                {loginText?.forgot_password}
                            </a>
                        </div>

                        {/* reCAPTCHA */}
                        <div className="recaptcha-wrapper mb-3 text-center w-100">
                            <ReCAPTCHA
                                ref={recaptchaRef}
                                sitekey={
                                    process.env.NODE_ENV === "production"
                                        ? "6LdYZLwrAAAAANCHZ8e4ejZXxqNOLmaombT4CP_J"
                                        : "6LctBPkqAAAAAJYBGX2n-BfA515UipewEkWbN9ty"
                                }
                                onChange={(token) => setCaptchaToken(token)}
                            />
                        </div>

                        {/* Create Account Button */}
                        <button
                            onClick={handleSubmit}
                            disabled={isSubmitting}
                            className="cta-btn w-100 d-flex justify-content-center"
                        >
                            {isSubmitting ? "Please wait..." : loginText?.Create_account}
                        </button>
                    </>
                )}
            </div>
        </div>
    );
}
